import React, { useEffect, useState } from 'react';
import { whenLabel } from '../shopOrderTemplates';
import { jobLabel } from '../utils/ui';
import SearchSelect from '../components/SearchSelect';
import { WhenPicker, assetOptions, assetLabel } from './RequestTruckFields';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path) { return fetch(`${API_BASE}${path}`, { headers: { 'x-worker-token': getToken() } }).then(r => r.ok ? r.json() : []); }

// "Reserve Equipment" — any truck, trailer or machine, for a person, a day and a time
// (Ryan, Oct 1 2026: "Alex needs the big back trailer tomorrow at 9 AM", "Pat needs the
// large crane Friday p.m."). Same shape on the phone and the office board.
//   details: asset_id / asset_label / custom_asset, for_user_id / for_name, needed_time,
//            until_date, purpose.   neededBy / onNeededBy: the order's date.
//   onEquipment: sets the order's equipment_id so the asset's own card lists the reservation.
//   people: the user list when the caller already has it (the board); otherwise fetched.
//   selfId: who's filling the form — the default person.
export default function ReserveAssetFields({ details, setDetail, projectId, onProject, mobile, neededBy, onNeededBy, onEquipment, people: peopleIn, selfId, orderId }) {
  const [equipment, setEquipment] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [people, setPeople] = useState(peopleIn || []);
  const [clashes, setClashes] = useState([]);

  useEffect(() => {
    apiFetch('/equipment').then(d => setEquipment(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch('/projects').then(d => setJobs(Array.isArray(d) ? d : [])).catch(() => {});
    if (!peopleIn) apiFetch('/users').then(d => setPeople(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (peopleIn) setPeople(peopleIn); }, [peopleIn]);

  // Default the person to whoever is filling the form.
  useEffect(() => {
    if (!details.for_user_id && selfId && people.length) {
      const me = people.find(p => p.id === selfId);
      if (me) { setDetail('for_user_id', me.id); setDetail('for_name', `${me.firstName || ''} ${me.lastName || ''}`.trim()); }
    }
  }, [people, selfId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Who else has this asset spoken for around the same day — so two people don't both
  // count on the big back trailer Thursday morning.
  useEffect(() => {
    if (!details.asset_id || !neededBy) { setClashes([]); return; }
    let live = true;
    apiFetch(`/shop-orders?equipmentId=${encodeURIComponent(details.asset_id)}`).then(rows => {
      if (!live) return;
      const from = String(neededBy).slice(0, 10); const to = String(details.until_date || neededBy).slice(0, 10);
      const list = (Array.isArray(rows) ? rows : []).filter(o => o.type === 'request_equipment' && o.work_status !== 'complete' && o.id !== orderId && o.needed_by).filter(o => {
        let d = {}; try { d = JSON.parse(o.details || '{}') || {}; } catch {}
        const oFrom = String(o.needed_by).slice(0, 10); const oTo = String(d.until_date || o.needed_by).slice(0, 10);
        return oFrom <= to && oTo >= from;
      }).map(o => { let d = {}; try { d = JSON.parse(o.details || '{}') || {}; } catch {} return { id: o.id, who: d.for_name || o.requester_name || 'someone', when: whenLabel(o.needed_by, d.needed_time), until: d.until_date && d.until_date !== o.needed_by ? whenLabel(d.until_date) : '' }; });
      setClashes(list);
    }).catch(() => setClashes([]));
    return () => { live = false; };
  }, [details.asset_id, neededBy, details.until_date, orderId]);

  const fontSize = mobile ? 16 : 13;
  const inp = { border: '1px solid #d1d5db', borderRadius: mobile ? 10 : 6, padding: mobile ? '12px' : '8px 10px', fontSize, width: '100%', boxSizing: 'border-box', background: '#fff' };
  const lbl = { fontSize: mobile ? 12 : 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' };
  const row = { marginBottom: mobile ? 12 : 10 };
  const hint = { fontSize: 11, color: '#9ca3af', marginTop: 4 };

  const byName = (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
  const peopleOpts = [{ value: '', label: '— Who needs it —' }, ...[...people].sort(byName).map(p => ({ value: p.id, label: `${p.firstName || ''} ${p.lastName || ''}`.trim(), sub: p.role || '' }))];

  function pickAsset(v) {
    if (v === '__custom') { setDetail('asset_custom', true); setDetail('asset_id', ''); setDetail('asset_label', ''); if (onEquipment) onEquipment(''); return; }
    setDetail('asset_custom', false); setDetail('asset_id', v);
    const e = equipment.find(x => x.id === v);
    setDetail('asset_label', e ? assetLabel(e) : '');
    if (onEquipment) onEquipment(v);
  }
  const picked = equipment.find(x => x.id === details.asset_id);

  return (
    <div>
      <div style={row}>
        <label style={lbl}>What (any truck, trailer or machine)</label>
        <SearchSelect value={details.asset_custom ? '__custom' : (details.asset_id || '')} onChange={pickAsset}
          placeholder="Search by asset #, name or type…" inputStyle={inp} options={assetOptions(equipment, '— Pick the asset —')} />
        {picked && <div style={hint}>{picked.category ? picked.category + ' · ' : ''}📍 {picked.current_location || 'Shop'}{picked.assigned_to ? ' · 👤 ' + picked.assigned_to : ''}</div>}
        {details.asset_custom && <input value={details.custom_asset || ''} onChange={e => setDetail('custom_asset', e.target.value)} style={{ ...inp, marginTop: 6 }} placeholder="Describe it" />}
      </div>

      <div style={row}>
        <label style={lbl}>Who needs it</label>
        <SearchSelect value={details.for_user_id || ''} onChange={v => { const p = people.find(x => x.id === v); setDetail('for_user_id', v); setDetail('for_name', p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : ''); }}
          placeholder="Search people…" inputStyle={inp} options={peopleOpts} />
      </div>

      <div style={row}>
        <WhenPicker date={neededBy} onDate={onNeededBy} time={details.needed_time || ''} onTime={v => setDetail('needed_time', v)} mobile={mobile}
          label="Need it on" inp={inp} lbl={lbl} hint={hint} />
      </div>

      <div style={row}>
        <label style={lbl}>Until (optional)</label>
        <input type="date" value={details.until_date || ''} min={neededBy || undefined} onChange={e => setDetail('until_date', e.target.value)} style={{ ...inp, width: mobile ? '100%' : 170 }} />
        <div style={hint}>Leave blank for the one day.</div>
      </div>

      {clashes.length > 0 && (
        <div style={{ ...row, padding: '10px 12px', borderRadius: 10, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', fontSize: mobile ? 14 : 12.5 }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠️ Already spoken for around then</div>
          {clashes.map(c => <div key={c.id}>• {c.who} · {c.when}{c.until ? ' → ' + c.until : ''}</div>)}
          <div style={{ marginTop: 4, fontSize: mobile ? 12.5 : 11.5 }}>You can still send this — the shop will sort out who gets it.</div>
        </div>
      )}

      <div style={row}>
        <label style={lbl}>For which job (optional)</label>
        <SearchSelect value={projectId || ''} onChange={v => onProject && onProject(v)} placeholder="Search jobs…" inputStyle={inp}
          options={[{ value: '', label: '— No job —' }, ...jobs.map(p => ({ value: p.id, label: jobLabel(p) }))]} />
      </div>

      <div style={row}>
        <label style={lbl}>What for (optional)</label>
        <textarea value={details.purpose || ''} onChange={e => setDetail('purpose', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} placeholder="e.g. hauling the mini to 2633" />
      </div>
    </div>
  );
}
